import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { extractTitle } from "../../utils/extractTitle";
import { useFullscreen } from "../../contexts/FullscreenContext";
import { Header } from "../../components/layout/Header";
import { projectAPI, thoughtSessionAPI, outlineAPI, APIError } from "../../lib/api";
import { documentAPI, suggestionAPI } from "../../lib/documentApi";
import { AIStatusBanner } from "../../components/AIStatusBanner";
import { AISetupModal } from "../../components/ai/AISetupModal";
import { useAIConfig } from "../../contexts/AIConfigContext";
import { ProjectSettingsModal } from "../../components/projects/ProjectSettingsModal";
import { Modal } from "../../components/common/Modal";

/**
 * Returns true when the error signals a persistent AI configuration problem
 * (missing key, bad credentials, feature not enabled). These should surface
 * via the in-page banner rather than a transient toast.
 */
function isAIConfigError(err: unknown): boolean {
  if (err instanceof APIError) {
    const code = (err.data as { code?: string })?.code;
    return code === "AI_NOT_CONFIGURED" || code === "AI_AUTH_ERROR";
  }
  return false;
}

/**
 * Returns a user-friendly error message for AI-related API errors.
 */
function getAIErrorMessage(err: unknown): string {
  if (err instanceof APIError) {
    const data = err.data as
      | { code?: string; message?: string; retry_after_seconds?: number }
      | undefined;
    const code = data?.code;

    switch (code) {
      case "AI_NOT_CONFIGURED":
        return "AI features are not available right now. Please contact your administrator.";
      case "RATE_LIMIT_EXCEEDED": {
        const retryAfter = data?.retry_after_seconds;
        return retryAfter
          ? `You've hit the rate limit. Please wait ${retryAfter} seconds and try again.`
          : "You've hit the rate limit. Please wait a moment and try again.";
      }
      case "AI_OVERLOADED":
        return "The AI service is temporarily overloaded. Please try again in a moment.";
      case "AI_AUTH_ERROR":
        return "There's a configuration issue with the AI service. Please contact support.";
      case "AI_UPSTREAM_ERROR":
      case "AI_SERVICE_ERROR":
        return "The AI service encountered an error. Please try again.";
      default:
        break;
    }

    // Fallback based on status code
    if (err.statusCode === 503)
      return "AI service is currently unavailable. Please try again later.";
    if (err.statusCode === 429) return "Too many requests. Please wait a moment and try again.";
    if (err.statusCode === 502) return "The AI service encountered an error. Please try again.";
  }

  return err instanceof Error ? err.message : "Something went wrong. Please try again.";
}
import { StreamChat, type ChatMessage, type OutlineItem } from "../../components/stream/StreamChat";
import { StreamSidebar } from "../../components/stream/StreamSidebar";
import { OutlineEditor } from "../../components/outline/OutlineEditor";
import {
  RichTextEditor,
  SuggestionControls,
  FocusModeToggle,
  ZenModeToggle,
  RealTimeSuggestionIndicator,
  extractPlainText,
  textToLexicalState,
  countText,
  type Suggestion,
  type SuggestionCategory,
} from "../../components/editor";
import { VersionHistorySidebar } from "../../components/version";
import { ExportDropdown, FORMAT_DISPLAY_NAMES } from "../../components/export";
import { useRealTimeSuggestions } from "../../hooks/useRealTimeSuggestions";
import type {
  ProjectResponse,
  ProjectMode,
  ThoughtSessionResponse,
  DocumentResponse,
  SuggestionResponse,
  OutlineResponse,
  OutlineNodeResponse,
} from "../../types/api";
import type { OutlineNodeData } from "../../components/outline/OutlineNode";
import type { SerializedEditorState } from "lexical";
import styles from "./ProjectDetail.module.css";

const modeI18nKeys: Record<ProjectMode, { label: string; description: string }> = {
  stream: {
    label: "modes.stream",
    description: "projectDetail.streamDescription",
  },
  outline: {
    label: "modes.outline",
    description: "projectDetail.outlineDescription",
  },
  edit: {
    label: "modes.draft",
    description: "projectDetail.editDescription",
  },
};

export default function ProjectDetail() {
  const { id: orgId, projectId } = useParams<{
    id: string;
    projectId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isFullscreen, exitFullscreen } = useFullscreen();

  // Mobile UI state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [project, setProject] = useState<ProjectResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  // Track the latest auto-save timestamp.  A lightweight state update here is
  // safe — the render cascade in #1255 was caused by mutating `project` (which
  // triggered heavy dependent effects), not by a simple timestamp string change.
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const documentContentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset auto-save timestamp and cancel any pending debounced content update
  // when navigating between projects so the UI doesn't display the previous
  // project's save time and stale timers don't fire for the wrong document.
  useEffect(() => {
    setLastSavedAt(null);
    if (documentContentTimerRef.current) {
      clearTimeout(documentContentTimerRef.current);
      documentContentTimerRef.current = null;
    }
  }, [projectId]);

  const [session, setSession] = useState<ThoughtSessionResponse | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showOutlinePrompt, setShowOutlinePrompt] = useState(false);

  // Edit mode state
  const [document, setDocument] = useState<DocumentResponse | null>(null);
  // Compute the most recent timestamp across all available sources so that a
  // session or project update newer than the last auto-save isn't hidden.
  const latestSavedTimestamp = useMemo(() => {
    const candidates = [lastSavedAt, session?.updated_at, project?.updated_at].filter(
      Boolean,
    ) as string[];
    if (candidates.length === 0) return undefined;
    return candidates.reduce((a, b) => (new Date(a) >= new Date(b) ? a : b));
  }, [lastSavedAt, session?.updated_at, project?.updated_at]);

  const [documentLoading, setDocumentLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionResponse[]>([]);
  const [activeFilters, setActiveFilters] = useState<SuggestionCategory[]>([
    "grammar",
    "clarity",
    "structure",
    "style",
    "logic",
  ]);
  const [focusModeEnabled, setFocusModeEnabled] = useState(false);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  const [documentContent, setDocumentContent] = useState("");
  const [editorKey, setEditorKey] = useState(0);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsDeleting, setSettingsDeleting] = useState(false);

  // AI configuration status — context provides the baseline; local override
  // allows AI errors within this page to immediately surface the CTA.
  const {
    aiConfigured: contextAiConfigured,
    aiEnabled,
    aiHasConfig,
    aiNeedsVerification,
    refreshAIStatus,
  } = useAIConfig();
  const [aiConfiguredOverride, setAiConfiguredOverride] = useState<boolean | null>(null);
  const rawAiConfigured = aiConfiguredOverride ?? contextAiConfigured;
  // When AI is disabled by the user, suppress all "not configured" warnings
  // by pretending it's configured. AI features still won't fire because
  // the server has no provider, but the UI stays clean.
  // Also treat aiHasConfig as sufficient — a platform-managed API key may not
  // have been explicitly verified yet, but the banner shouldn't nag users.
  const aiConfigured = aiEnabled
    ? rawAiConfigured === true || aiHasConfig
      ? true
      : rawAiConfigured
    : true;
  const [showAISetupModal, setShowAISetupModal] = useState(false);
  const navigateToAISettings = useCallback(() => setShowAISetupModal(true), []);
  const openSettings = () => setShowSettings(true);
  const closeSettings = useCallback(() => {
    setShowSettings(false);
    // Re-fetch the true server state so the page reflects current config.
    refreshAIStatus();
  }, [refreshAIStatus]);
  const setAiConfigured = setAiConfiguredOverride;

  // Outline mode state
  const [outline, setOutline] = useState<OutlineResponse | null>(null);
  // Keep a ref to the latest outline so import/sync callbacks never read stale
  // state (avoids React batching / closure timing issues — fixes #1454).
  const outlineRef = useRef<OutlineResponse | null>(null);
  outlineRef.current = outline;
  const [outlineLoading, setOutlineLoading] = useState(false);
  const [outlineGenerating, setOutlineGenerating] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [draftStreamSidebarOpen, setDraftStreamSidebarOpen] = useState(false);

  useEffect(() => {
    async function loadProject() {
      if (!orgId || !projectId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await projectAPI.getById(orgId, projectId);
        setProject(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load project");
      } finally {
        setLoading(false);
      }
    }

    loadProject();
  }, [orgId, projectId]);

  const loadSession = useCallback(async () => {
    if (!orgId || !projectId) return;

    setSessionLoading(true);
    try {
      const sessionData = await thoughtSessionAPI.getOrCreate(orgId, projectId);
      setSession(sessionData);

      if (sessionData.messages.length >= 6) {
        setShowOutlinePrompt(true);
      }
    } catch (err) {
      console.error("Failed to load session:", err);
    } finally {
      setSessionLoading(false);
    }
  }, [orgId, projectId]);

  // Track if we've processed the initial thought
  const [initialThoughtSent, setInitialThoughtSent] = useState(false);
  // Counter that increments when outline seeding completes (success or failure).
  // Used as a dependency in the outline-load effect so it re-runs after seeding.
  const [outlineSeedingDone, setOutlineSeedingDone] = useState(0);
  // Ref that is true only while outline seeding is in-flight, so we can
  // suppress the outline-load effect without permanently disabling it.
  const outlineSeedingRef = useRef(false);

  useEffect(() => {
    if (
      project?.current_mode === "stream" ||
      project?.current_mode === "edit" ||
      project?.current_mode === "outline"
    ) {
      loadSession();
    }
  }, [project?.current_mode, loadSession]);

  // Load document and suggestions for Edit mode
  const loadDocument = useCallback(async () => {
    if (!orgId || !projectId) return;

    setDocumentLoading(true);
    try {
      const doc = await documentAPI.getByProjectId(orgId, projectId);
      setDocument(doc);

      // Initialise plain-text content so the word count is accurate even
      // before the Draft editor mounts (i.e. on Stream / Outline tabs).
      if (doc.content) {
        const plain = extractPlainText(doc.content as SerializedEditorState);
        setDocumentContent(plain);
      }

      // Load suggestions for the document (non-critical — don't fail the whole load)
      try {
        const suggestionsData = await suggestionAPI.getByDocumentId(orgId, doc.id);
        setSuggestions(suggestionsData.filter((s) => s.status === "pending"));
      } catch {
        // Suggestions may not exist yet for new documents
      }
    } catch (err) {
      console.error("Failed to load document:", err);
    } finally {
      setDocumentLoading(false);
    }
  }, [orgId, projectId]);

  // Load document once when project first becomes available (not on every project state change).
  // Re-fetching on every `project` update caused a render cascade: auto-save → setProject
  // → useEffect → loadDocument → setDocument → re-render loop (see #1255).
  const hasLoadedDocumentRef = useRef(false);
  const loadedProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    // Reset the gate when the project changes so we reload the document for
    // different projects rendered by the same component instance (review feedback).
    if (projectId && projectId !== loadedProjectIdRef.current) {
      hasLoadedDocumentRef.current = false;
    }
    if (project && !hasLoadedDocumentRef.current) {
      hasLoadedDocumentRef.current = true;
      loadedProjectIdRef.current = projectId ?? null;
      loadDocument();
    }
  }, [project, projectId, loadDocument]);

  // Load outline for Outline mode
  const loadOutline = useCallback(async () => {
    if (!orgId || !projectId) return;

    setOutlineLoading(true);
    try {
      const outlineData = await outlineAPI.get(orgId, projectId);
      setOutline(outlineData);
    } catch (err) {
      console.error("Failed to load outline:", err);
    } finally {
      setOutlineLoading(false);
    }
  }, [orgId, projectId]);

  // Always load outline content so word count is accurate across all views.
  // Skip loading when there is initial content waiting to be seeded from the
  // quick-start flow — the initial-thought effect will create the outline via
  // PUT and call setOutline itself.
  useEffect(() => {
    if (project) {
      const hasInitialContent = sessionStorage.getItem(`project_${projectId}_initial_thought`);
      const initialMode = sessionStorage.getItem(`project_${projectId}_initial_mode`);
      // Skip loading when outline-mode initial content is pending (sessionStorage
      // still set) or outline seeding is currently in-flight. The seeding effect
      // calls setOutline itself, so an extra load would race against it and
      // overwrite the seeded nodes with the stale server state.
      // Stream/draft modes don't seed the outline client-side, so allow the
      // load to proceed even when sessionStorage is set for those modes.
      const outlineSeeding = hasInitialContent && initialMode === "outline";
      if (!outlineSeeding && !outlineSeedingRef.current) {
        loadOutline();
      }
    }
    // outlineSeedingDone triggers a re-run after seeding completes so the
    // effect can fetch the server outline if it was skipped during seeding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, projectId, loadOutline, outlineSeedingDone]);

  // Handle new suggestions from real-time generation
  const handleNewSuggestions = useCallback((newSuggestions: SuggestionResponse[]) => {
    setSuggestions((prev) => {
      // Merge new suggestions with existing, avoiding duplicates
      const existingIds = new Set(prev.map((s) => s.id));
      const uniqueNew = newSuggestions.filter(
        (s) => !existingIds.has(s.id) && s.status === "pending",
      );
      return [...prev, ...uniqueNew];
    });
  }, []);

  // Real-time suggestions hook — gate on aiConfigured so we never fire
  // requests when the org has no AI provider configured.
  const {
    isGenerating,
    lastGeneratedAt,
    error: realTimeError,
    isAINotConfigured: realTimeAINotConfigured,
  } = useRealTimeSuggestions({
    documentId: document?.id ?? null,
    orgId: orgId ?? null,
    content: documentContent,
    enabled:
      realTimeEnabled &&
      aiEnabled &&
      aiConfigured === true &&
      project?.current_mode === "edit" &&
      !focusModeEnabled,
    debounceMs: 500,
    onSuggestionsGenerated: handleNewSuggestions,
  });

  // If the hook discovers at runtime that AI is broken (auth error, 404, etc.)
  // propagate that to the page-level AI state so the CTA surfaces immediately.
  useEffect(() => {
    if (realTimeAINotConfigured) {
      setAiConfiguredOverride(false);
    }
  }, [realTimeAINotConfigured]);

  const handleModeChange = useCallback(
    async (mode: ProjectMode) => {
      if (!orgId || !projectId || !project || project.current_mode === mode) return;

      const previousMode = project.current_mode;

      // Optimistically update the mode so tabs respond instantly
      setProject((prev) => (prev ? { ...prev, current_mode: mode } : prev));
      setUpdating(true);

      try {
        const updated = await projectAPI.update(orgId, projectId, {
          currentMode: mode,
        });
        setProject(updated);
        toast.success(
          t("projectDetail.switchedToMode", {
            mode: t(modeI18nKeys[mode].label),
          }),
        );
      } catch (err) {
        // Revert to previous mode on failure
        setProject((prev) => (prev ? { ...prev, current_mode: previousMode } : prev));
        toast.error(err instanceof Error ? err.message : "Failed to update mode");
      } finally {
        setUpdating(false);
      }
    },
    [orgId, projectId, project, t],
  );

  // State for streaming response
  const [streamingResponse, setStreamingResponse] = useState("");

  const handleSendMessage = async (content: string) => {
    if (!session || !orgId) return;

    // Optimistically add user message to UI
    const userMessage = {
      role: "user" as const,
      content,
      timestamp: new Date().toISOString(),
    };
    setSession((prev) =>
      prev
        ? {
            ...prev,
            messages: [...prev.messages, userMessage],
            updated_at: new Date().toISOString(),
          }
        : prev,
    );

    setIsTyping(true);
    setStreamingResponse("");

    // Track whether the streaming onError callback already handled the failure
    // so the outer catch doesn't duplicate the error message.
    let handledByStream = false;

    try {
      const updatedSession = await thoughtSessionAPI.addMessageWithStream(
        orgId,
        session.id,
        content,
        {
          onChunk: (text) => {
            setStreamingResponse((prev) => prev + text);
          },
          onDone: (finalSession) => {
            setSession(finalSession);
            setStreamingResponse("");
            setIsTyping(false);

            if (finalSession.messages.length >= 6 && !showOutlinePrompt) {
              setShowOutlinePrompt(true);
            }
          },
          onError: (err) => {
            console.error("Stream error:", err);
            handledByStream = true;
            setStreamingResponse("");
            setIsTyping(false);
            if (isAIConfigError(err)) {
              setAiConfigured(false);
            } else {
              toast.error(getAIErrorMessage(err), { id: "ai-error" });
            }
          },
        },
      );

      // Final update with complete session from server
      setSession(updatedSession);
      setStreamingResponse("");
      setIsTyping(false);

      if (updatedSession.messages.length >= 6 && !showOutlinePrompt) {
        setShowOutlinePrompt(true);
      }
    } catch (err) {
      // Only handle if the streaming callback didn't already deal with it
      if (handledByStream) return;
      console.error("Failed to send message:", err);
      setStreamingResponse("");
      setIsTyping(false);
      if (isAIConfigError(err)) {
        setAiConfigured(false);
      } else {
        toast.error(getAIErrorMessage(err), { id: "ai-error" });
      }
    }
  };

  const handleEditMessage = async (index: number, content: string) => {
    if (!orgId || !session) return;
    try {
      const updatedSession = await thoughtSessionAPI.editMessage(orgId, session.id, index, content);
      setSession(updatedSession);
    } catch (err) {
      console.error("Failed to edit message:", err);
      toast.error("Failed to edit message");
    }
  };

  const handleDeleteMessage = async (index: number) => {
    if (!orgId || !session) return;
    try {
      const updatedSession = await thoughtSessionAPI.deleteMessage(orgId, session.id, index);
      setSession(updatedSession);
    } catch (err) {
      console.error("Failed to delete message:", err);
      toast.error("Failed to delete message");
    }
  };

  // Check for initial thought from quick-start and auto-send it.
  // Routes content to the correct view based on the selected quick-start mode.
  useEffect(() => {
    if (!projectId || !project || initialThoughtSent) return;

    const initialThought = sessionStorage.getItem(`project_${projectId}_initial_thought`);
    if (!initialThought) return;

    const initialMode = sessionStorage.getItem(`project_${projectId}_initial_mode`);

    // For draft mode, wait until the document is loaded before proceeding.
    // The effect will re-run once `document` is populated.
    if (initialMode === "draft" && !document) return;

    // Stream mode sends the initial thought as an AI message, so it needs
    // the session to be ready. Outline and draft modes seed content directly
    // via their own APIs and don't require the AI session. Fixes #847.
    const needsSession = !initialMode || initialMode === "stream";
    if (needsSession && (!session || sessionLoading || isTyping)) return;

    // Clear the stored values
    sessionStorage.removeItem(`project_${projectId}_initial_thought`);
    sessionStorage.removeItem(`project_${projectId}_initial_mode`);

    // Mark as sent to prevent re-sending
    setInitialThoughtSent(true);

    // Map quick-start mode names to backend ProjectMode values.
    // The dashboard stores "draft" in sessionStorage but the backend expects "edit".
    const modeMap: Record<string, ProjectMode> = {
      stream: "stream",
      outline: "outline",
      draft: "edit",
      edit: "edit",
    };
    const mappedMode = initialMode ? (modeMap[initialMode] ?? (initialMode as ProjectMode)) : null;

    // Apply the selected mode if different from current
    if (mappedMode && mappedMode !== project?.current_mode) {
      handleModeChange(mappedMode);
    }

    // Route the initial content to the appropriate view
    if (initialMode === "draft" && orgId && document) {
      // Seed the draft editor with the text as a Lexical document
      const lexicalState = textToLexicalState(initialThought);
      const { words, characters } = countText(initialThought);
      documentAPI
        .update(orgId, document.id, {
          content: lexicalState,
          wordCount: words,
          characterCount: characters,
        })
        .then((updatedDoc) => {
          setDocument(updatedDoc);
          setDocumentContent(initialThought);
          // Force the editor to remount so it picks up the seeded content.
          // The RichTextEditor only reads initialContent on mount, so without
          // a key change the editor stays empty.
          setEditorKey((k) => k + 1);
        })
        .catch((err) => console.error("Failed to seed draft:", err));
    } else if (initialMode === "outline" && orgId) {
      // Seed the outline so the user doesn't land on an empty view.
      // Split by newlines so each line becomes its own outline item.
      // Fixes #1072.
      const lines = initialThought
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      const MAX_TITLE_LENGTH = 80;
      const MAX_TITLE_WORDS = 10;
      const nodes = (lines.length > 0 ? lines : [initialThought]).map((line) => {
        let content = line;
        let expanded: string | undefined;

        const wordCount = line.split(/\s+/).filter(Boolean).length;
        if (line.length > MAX_TITLE_LENGTH || wordCount > MAX_TITLE_WORDS) {
          // Prose-like text (long or many words) goes entirely into the
          // body/notes area with a generated title, so the section title
          // stays readable. Fixes #1271, #1275, #1374.
          content = extractTitle(line, { maxLength: MAX_TITLE_LENGTH, maxWords: MAX_TITLE_WORDS });
          expanded = line;
        }

        return {
          id: crypto.randomUUID(),
          content,
          ...(expanded ? { expanded } : {}),
          children: [],
          isAISuggested: false,
        };
      });
      outlineSeedingRef.current = true;
      // Capture current project so the catch handler can detect navigation.
      const seedProjectId = projectId;
      outlineAPI
        .update(orgId, projectId, nodes)
        .then((saved) => setOutline(saved))
        .catch((err) => {
          console.error("Failed to seed outline:", err);
          // Only fall back to server fetch if we're still on the same project.
          // If the user navigated away, the new project's own effect will load
          // its outline; fetching here would race and overwrite with stale data.
          if (seedProjectId === projectId) {
            loadOutline();
          }
        })
        .finally(() => {
          outlineSeedingRef.current = false;
          // Bump counter so the outline-load effect re-runs now that seeding
          // is no longer in-flight.
          setOutlineSeedingDone((c) => c + 1);
        });
    } else {
      // Default: send to the stream (stream mode or fallback).
      // The dashboard seeds stream content server-side via initialThought,
      // so check if the session already contains the message to avoid
      // sending a duplicate. Only re-send if the server-side seeding
      // failed (session is empty). Fixes #1547.
      const alreadySeeded = session?.messages?.some(
        (m) => m.role === "user" && m.content === initialThought,
      );
      if (!alreadySeeded) {
        handleSendMessage(initialThought);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectId,
    session,
    sessionLoading,
    isTyping,
    initialThoughtSent,
    project?.current_mode,
    document,
  ]);

  // Keyboard shortcuts for switching views in Zen mode (Fixes #1450)
  // Supports multiple modifier combos because Ctrl+1/2/3 is intercepted by
  // most browsers for tab switching:
  //   - Cmd/Ctrl+1/2/3 (works in fullscreen on some browsers)
  //   - Alt+1/2/3 (reliable fallback that isn't intercepted)
  // Also matches by `e.code` (Digit1/2/3) for keyboard-layout independence.
  useEffect(() => {
    const modeByKey: Record<string, ProjectMode> = {
      "1": "stream",
      "2": "outline",
      "3": "edit",
    };

    const modeByCode: Record<string, ProjectMode> = {
      Digit1: "stream",
      Digit2: "outline",
      Digit3: "edit",
    };

    const currentMode = project?.current_mode;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isFullscreen) return;

      // Accept Cmd/Ctrl or Alt as the modifier (not both, no Shift).
      const hasCtrlOrMeta = e.metaKey || e.ctrlKey;
      const hasAlt = e.altKey;
      if (!(hasCtrlOrMeta || hasAlt)) return;
      if (e.shiftKey) return;
      // Don't fire when both Ctrl and Alt are held (AltGr on Windows sends
      // Ctrl+Alt together, which would mis-trigger).
      if (hasCtrlOrMeta && hasAlt) return;

      const mode = modeByKey[e.key] ?? modeByCode[e.code];
      if (!mode) return;

      // Always prevent the default action (e.g. typing a digit into an input)
      // even when the target mode is already active.
      e.preventDefault();

      if (mode !== currentMode) {
        handleModeChange(mode);
        toast(
          `Switched to ${mode === "edit" ? "Draft" : mode.charAt(0).toUpperCase() + mode.slice(1)}`,
          { duration: 1500, icon: "🔄", position: "bottom-center" },
        );
      }
    };

    // Use the capture phase so the handler fires before input elements can
    // consume the event (e.g. typing a digit into the chat textarea).
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [isFullscreen, project?.current_mode, handleModeChange]);

  const handleTransitionToOutline = async () => {
    if (!orgId || !projectId || outlineGenerating) return;

    setOutlineGenerating(true);
    try {
      const generatedOutline = await outlineAPI.generate(orgId, projectId, {
        allSessions: true,
      });
      setOutline(generatedOutline);
      await handleModeChange("outline");
    } catch (err) {
      console.error("Failed to generate outline:", err);
      if (isAIConfigError(err)) {
        setAiConfigured(false);
        toast.error("Please configure a valid API key in Settings to use AI features.", {
          id: "ai-config-error",
        });
        navigateToAISettings();
      } else {
        toast.error(getAIErrorMessage(err), { id: "ai-error" });
      }
    } finally {
      setOutlineGenerating(false);
    }
  };

  // Convert API outline nodes to component format
  const convertToOutlineNodeData = useCallback(
    (nodes: OutlineNodeResponse[]): OutlineNodeData[] => {
      return nodes.map((node) => ({
        id: node.id,
        content: node.content,
        note: node.expanded || undefined,
        children: node.children ? convertToOutlineNodeData(node.children) : undefined,
        isAiGenerated: node.isAISuggested ?? false,
        sourceStreamContent: node.sourceStreamContent,
        nodeType: node.nodeType,
      }));
    },
    [],
  );

  // Convert component format back to API format
  const convertToOutlineNodeResponse = useCallback(
    (nodes: OutlineNodeData[]): OutlineNodeResponse[] => {
      return nodes.map((node) => ({
        id: node.id,
        content: node.content,
        expanded: node.note || undefined,
        children: node.children ? convertToOutlineNodeResponse(node.children) : [],
        isAISuggested: node.isAiGenerated,
        sourceStreamContent: node.sourceStreamContent,
        nodeType: node.nodeType,
      }));
    },
    [],
  );

  // Guard against stale API responses overwriting newer optimistic state.
  const outlineSaveVersionRef = useRef(0);
  // Debounce timer for outline saves — prevents rate-limiting during normal typing
  const outlineSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const OUTLINE_SAVE_DEBOUNCE_MS = 1500;

  // Track pending (unsaved) outline data so we can flush on unmount / beforeunload
  const pendingOutlineSaveRef = useRef<{
    orgId: string;
    projectId: string;
    nodes: OutlineNodeResponse[];
  } | null>(null);

  // Flush any pending debounced outline save. Uses `keepalive` so the request
  // survives page unload. Fire-and-forget: callers don't await the result.
  const flushOutlineSave = useCallback(() => {
    const pending = pendingOutlineSaveRef.current;
    if (!pending) return;
    pendingOutlineSaveRef.current = null;

    if (outlineSaveTimerRef.current) {
      clearTimeout(outlineSaveTimerRef.current);
      outlineSaveTimerRef.current = null;
    }

    const baseURL = import.meta.env.VITE_API_URL || "/api";
    const url = `${baseURL}/organizations/${pending.orgId}/projects/${pending.projectId}/outline`;

    // Use fetch with keepalive so the browser keeps the request alive even
    // during page navigation / tab close.
    try {
      fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        keepalive: true,
        body: JSON.stringify({ nodes: pending.nodes }),
      });
    } catch {
      // Best-effort — nothing we can do if it fails during unload
    }
  }, []);

  // Handle outline changes from the editor
  const handleOutlineChange = useCallback(
    (nodes: OutlineNodeData[]) => {
      if (!orgId || !projectId) return;

      const apiNodes = convertToOutlineNodeResponse(nodes);

      // Optimistically update local state immediately (no delay)
      setOutline((prev) =>
        prev
          ? { ...prev, nodes: apiNodes, updated_at: new Date().toISOString() }
          : {
              id: "",
              project_id: projectId,
              nodes: apiNodes,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
      );

      // Track this as the latest pending save
      pendingOutlineSaveRef.current = { orgId, projectId, nodes: apiNodes };

      // Debounce the API persist to avoid rate-limiting during rapid edits
      if (outlineSaveTimerRef.current) {
        clearTimeout(outlineSaveTimerRef.current);
      }
      outlineSaveTimerRef.current = setTimeout(async () => {
        pendingOutlineSaveRef.current = null;
        const saveVersion = ++outlineSaveVersionRef.current;
        try {
          const saved = await outlineAPI.update(orgId, projectId, apiNodes);
          // Only apply the server response if no newer save has started
          if (outlineSaveVersionRef.current === saveVersion) {
            setOutline(saved);
          }
        } catch (err) {
          console.error("Failed to save outline:", err);
        }
      }, OUTLINE_SAVE_DEBOUNCE_MS);
    },
    [orgId, projectId, convertToOutlineNodeResponse],
  );

  // Handle deleting a single node via the dedicated DELETE endpoint.
  // Flush pending outline save on unmount / navigation so edits aren't lost.
  // Also listen for beforeunload to catch browser refresh and tab close.
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushOutlineSave();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      flushOutlineSave();
    };
  }, [flushOutlineSave]);

  // Auto-prune empty projects created via quick-start when the user navigates
  // away without adding any content. Fixes #1608.
  const projectRef = useRef(project);
  projectRef.current = project;
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const documentContentRef = useRef(documentContent);
  documentContentRef.current = documentContent;

  useEffect(() => {
    if (!orgId || !projectId) return;
    const isEmptyNew = sessionStorage.getItem(`project_${projectId}_is_empty_new`);
    if (!isEmptyNew) return;

    return () => {
      // On unmount, check if the project is still empty.
      const proj = projectRef.current;
      const sess = sessionRef.current;
      const docContent = documentContentRef.current;

      const hasDocContent = docContent.trim().length > 0;
      const hasSessionContent = sess?.messages?.some(
        (m) => m.role === "user" && m.content.trim().length > 0,
      );
      const hasWordCount = (proj?.word_count ?? 0) > 0;
      const hasOutlineContent = (outlineRef.current?.nodes?.length ?? 0) > 0;

      if (!hasDocContent && !hasSessionContent && !hasWordCount && !hasOutlineContent) {
        // Fire-and-forget delete; user has already navigated away.
        projectAPI.delete(orgId, projectId).catch(() => {
          // Silently ignore — worst case the empty project remains.
        });
      }

      sessionStorage.removeItem(`project_${projectId}_is_empty_new`);
    };
  }, [orgId, projectId]);

  // This avoids the race-prone full-tree PUT and lets the server
  // perform the removal atomically.
  const handleDeleteOutlineNode = useCallback(
    async (nodeId: string) => {
      if (!orgId || !projectId) return;

      // Cancel any pending debounced outline save to prevent a stale full-tree
      // PUT from racing with the DELETE and restoring the removed node.
      if (outlineSaveTimerRef.current) {
        clearTimeout(outlineSaveTimerRef.current);
        outlineSaveTimerRef.current = null;
      }
      pendingOutlineSaveRef.current = null;

      const saveVersion = ++outlineSaveVersionRef.current;

      // Optimistic removal
      setOutline((prev) => {
        if (!prev) return prev;
        const removeNode = (nodes: OutlineNodeResponse[]): OutlineNodeResponse[] =>
          nodes
            .filter((n) => n.id !== nodeId)
            .map((n) => ({ ...n, children: removeNode(n.children) }));
        return { ...prev, nodes: removeNode(prev.nodes), updated_at: new Date().toISOString() };
      });

      try {
        const saved = await outlineAPI.deleteNode(orgId, projectId, nodeId);
        if (outlineSaveVersionRef.current === saveVersion) {
          setOutline(saved);
        }
      } catch (err) {
        console.error("Failed to delete outline node:", err);
        toast.error("Failed to delete item");
        // Re-fetch to restore consistent state
        try {
          const outlineData = await outlineAPI.get(orgId, projectId);
          setOutline(outlineData);
        } catch {
          // ignore re-fetch failure
        }
      }
    },
    [orgId, projectId],
  );

  // Handle expanding a point with AI
  const handleExpandPoint = useCallback(
    async (nodeId: string, content: string): Promise<string> => {
      if (!orgId || !projectId) {
        throw new Error("Missing organization or project ID");
      }

      const updatedOutline = await outlineAPI.expandPoint(orgId, projectId, nodeId);
      setOutline(updatedOutline);

      // Find the expanded content from the node
      const findExpandedContent = (nodes: OutlineNodeResponse[]): string | null => {
        for (const node of nodes) {
          if (node.id === nodeId && node.expanded) {
            return node.expanded;
          }
          if (node.children.length > 0) {
            const found = findExpandedContent(node.children);
            if (found) return found;
          }
        }
        return null;
      };

      return findExpandedContent(updatedOutline.nodes) || content;
    },
    [orgId, projectId],
  );

  // Convert API suggestions to editor format
  const editorSuggestions: Suggestion[] = useMemo(() => {
    return suggestions
      .filter((s) => activeFilters.includes(s.category))
      .map((s) => ({
        id: s.id,
        textRange: { start: s.text_range_start, end: s.text_range_end },
        originalText: s.original_text,
        suggestedText: s.suggested_text,
        category: s.category,
        confidenceScore: s.confidence_score,
        explanation: s.explanation ?? undefined,
      }));
  }, [suggestions, activeFilters]);

  // Handle document content changes for real-time suggestions
  const handleGenerateDraft = async () => {
    if (!orgId || !projectId || generatingDraft) return;

    setGeneratingDraft(true);
    try {
      const doc = await documentAPI.generateDraft(orgId, projectId);
      setDocument(doc);
      if (doc?.content) {
        const plainText = extractPlainText(doc.content as SerializedEditorState);
        setDocumentContent(plainText);
      }
      setEditorKey((k) => k + 1);
      toast.success("Draft generated from your stream thoughts!");
    } catch (err) {
      if (isAIConfigError(err)) {
        setAiConfigured(false);
        toast.error("Please configure a valid API key in Settings to use AI features.", {
          id: "ai-config-error",
        });
        navigateToAISettings();
      } else {
        toast.error(getAIErrorMessage(err), { id: "ai-error" });
      }
    } finally {
      setGeneratingDraft(false);
    }
  };

  const [importingStream, setImportingStream] = useState(false);

  const handleImportFromStream = async (mode: "replace" | "append" = "replace") => {
    setShowStreamReimportConfirm(false);
    if (!orgId || !projectId || importingStream) return;

    setImportingStream(true);
    try {
      const doc = await documentAPI.importFromStream(orgId, projectId, mode);
      setDocument(doc);
      if (doc?.content) {
        const plainText = extractPlainText(doc.content as SerializedEditorState);
        setDocumentContent(plainText);
      }
      setEditorKey((k) => k + 1);
      setStreamSyncedManual(true);
      toast.success(
        mode === "append"
          ? "Stream content appended to your draft!"
          : "Stream content imported into your draft!",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to import stream content");
    } finally {
      setImportingStream(false);
    }
  };

  // --- Stream sync tracking (mirrors outline sync logic) ---
  const [showStreamReimportConfirm, setShowStreamReimportConfirm] = useState(false);
  const [streamSyncedManual, setStreamSyncedManual] = useState<boolean | null>(null);

  // The stream is considered "synced" when:
  //   1. The user just imported during this session (manual override), OR
  //   2. The document was last saved at or after the stream session's last edit.
  const streamSynced = useMemo(() => {
    if (streamSyncedManual !== null) return streamSyncedManual;
    if (!document || !session) return true; // no session → nothing to sync
    return new Date(document.updated_at) >= new Date(session.updated_at);
  }, [streamSyncedManual, document, session]);

  const [importingOutline, setImportingOutline] = useState(false);
  const [showReimportConfirm, setShowReimportConfirm] = useState(false);
  const [showOutlineSyncConfirm, setShowOutlineSyncConfirm] = useState(false);
  const [outlineSyncedManual, setOutlineSyncedManual] = useState<boolean | null>(null);

  // The outline is considered "synced" when:
  //   1. The user just imported during this session (manual override), OR
  //   2. The document was last saved at or after the outline's last edit (timestamp check).
  // This prevents the "outline has newer content" banner from flashing on page
  // load when the draft is already up-to-date, and also prevents it from
  // reappearing immediately after a successful import.
  const outlineSynced = useMemo(() => {
    if (outlineSyncedManual !== null) return outlineSyncedManual;
    if (!document || !outline) return false;
    return new Date(document.updated_at) >= new Date(outline.updated_at);
  }, [outlineSyncedManual, document, outline]);

  const requestImportFromStream = () => {
    if (documentContent.trim()) {
      setShowStreamReimportConfirm(true);
    } else {
      handleImportFromStream();
    }
  };

  const requestImportFromOutline = () => {
    if (documentContent.trim()) {
      setShowReimportConfirm(true);
    } else {
      handleImportFromOutline();
    }
  };

  const handleImportFromOutline = async (mode: "replace" | "append" = "replace") => {
    setShowReimportConfirm(false);
    if (!orgId || !projectId || importingOutline) return;

    setImportingOutline(true);
    try {
      const doc = await documentAPI.importFromOutline(orgId, projectId, mode);
      setDocument(doc);
      if (doc?.content) {
        const plainText = extractPlainText(doc.content as SerializedEditorState);
        setDocumentContent(plainText);
      }
      setEditorKey((k) => k + 1);
      setOutlineSyncedManual(true);
      toast.success(
        mode === "append"
          ? "Outline content appended to your draft!"
          : "Outline content imported into your draft!",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to import outline content");
    } finally {
      setImportingOutline(false);
    }
  };

  // Debounce document-content state updates so parent re-renders don't fire on
  // every keystroke, reducing jank on mobile (#1255).
  // (Ref declared above, near other state, so the projectId-reset effect can clear it.)
  const handleEditorChange = useCallback((editorState: SerializedEditorState) => {
    const plainText = extractPlainText(editorState);
    if (documentContentTimerRef.current) clearTimeout(documentContentTimerRef.current);
    documentContentTimerRef.current = setTimeout(() => setDocumentContent(plainText), 300);
  }, []);

  // Handle document auto-save
  const handleAutoSave = useCallback(
    async (editorState: SerializedEditorState, wordCount: number, characterCount: number) => {
      if (!orgId || !document) return;

      const savedDocId = document.id;
      try {
        const savedDoc = await documentAPI.update(orgId, savedDocId, {
          content: editorState,
          wordCount,
          characterCount,
        });
        // Only update the timestamp if we're still viewing the same document —
        // an in-flight save from a previous project shouldn't overwrite the
        // current project's timestamp (review feedback).
        if (savedDoc?.updated_at && document?.id === savedDocId) {
          setLastSavedAt(savedDoc.updated_at);
        }
      } catch (err) {
        console.error("Failed to auto-save document:", err);
      }
    },
    [orgId, document],
  );

  // Handle accepting a suggestion
  const handleAcceptSuggestion = useCallback(
    async (suggestion: Suggestion) => {
      if (!orgId || !document) return;

      try {
        await suggestionAPI.accept(orgId, document.id, suggestion.id);
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        toast.success("Suggestion accepted");
      } catch (err) {
        console.error("Failed to accept suggestion:", err);
        toast.error("Failed to accept suggestion");
      }
    },
    [orgId, document],
  );

  // Handle rejecting a suggestion
  const handleRejectSuggestion = useCallback(
    async (suggestion: Suggestion) => {
      if (!orgId || !document) return;

      try {
        await suggestionAPI.reject(orgId, document.id, suggestion.id);
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        toast.success("Suggestion dismissed");
      } catch (err) {
        console.error("Failed to reject suggestion:", err);
        toast.error("Failed to dismiss suggestion");
      }
    },
    [orgId, document],
  );

  // Handle bulk accept/reject
  const handleAcceptAll = useCallback(
    async (category?: SuggestionCategory) => {
      if (!orgId || !document) return;

      const toAccept = category
        ? suggestions.filter((s) => s.category === category && s.status === "pending")
        : suggestions.filter((s) => s.status === "pending");

      let successCount = 0;
      for (const s of toAccept) {
        try {
          await suggestionAPI.accept(orgId, document.id, s.id);
          successCount++;
        } catch (err) {
          console.error(`Failed to accept suggestion ${s.id}:`, err);
        }
      }

      setSuggestions((prev) => prev.filter((s) => !toAccept.some((a) => a.id === s.id)));

      if (successCount > 0) {
        toast.success(`Accepted ${successCount} suggestion${successCount > 1 ? "s" : ""}`);
      }
    },
    [orgId, document, suggestions],
  );

  const handleRejectAll = useCallback(
    async (category?: SuggestionCategory) => {
      if (!orgId || !document) return;

      const toReject = category
        ? suggestions.filter((s) => s.category === category && s.status === "pending")
        : suggestions.filter((s) => s.status === "pending");

      let successCount = 0;
      for (const s of toReject) {
        try {
          await suggestionAPI.reject(orgId, document.id, s.id);
          successCount++;
        } catch (err) {
          console.error(`Failed to reject suggestion ${s.id}:`, err);
        }
      }

      setSuggestions((prev) => prev.filter((s) => !toReject.some((r) => r.id === s.id)));

      if (successCount > 0) {
        toast.success(`Dismissed ${successCount} suggestion${successCount > 1 ? "s" : ""}`);
      }
    },
    [orgId, document, suggestions],
  );

  const getWordCount = useCallback(() => {
    const countWords = (text: string) =>
      text.trim()
        ? text
            .trim()
            .split(/\s+/)
            .filter((w) => w.length > 0).length
        : 0;

    const mode = project?.current_mode;

    // Return word count scoped to the active view
    if (mode === "edit") {
      return countWords(documentContent);
    }

    if (mode === "outline") {
      if (outline && outline.nodes.length > 0) {
        const collectText = (nodes: OutlineNodeResponse[]): string =>
          nodes
            .map((n) => [n.content, n.expanded || "", collectText(n.children || [])].join(" "))
            .join(" ");
        return countWords(collectText(outline.nodes));
      }
      return 0;
    }

    // Stream view
    if (session) {
      const userMessages = session.messages.filter((m) => m.role === "user");
      return countWords(userMessages.map((m) => m.content).join(" "));
    }

    return 0;
  }, [session, documentContent, outline, project?.current_mode]);

  const hasStreamContent = useMemo(() => {
    if (!session?.messages) return false;
    return session.messages.some((m) => m.role === "user" && m.content.trim().length > 0);
  }, [session]);

  const hasOutlineContent = useMemo(() => {
    return outline !== null && outline.nodes.length > 0;
  }, [outline]);

  // Convert outline nodes to the flat OutlineItem format for Stream view
  const streamOutlineItems: OutlineItem[] = useMemo(() => {
    if (!outline || outline.nodes.length === 0) return [];
    const convert = (nodes: OutlineNodeResponse[]): OutlineItem[] =>
      nodes.map((n) => ({
        id: n.id,
        content: n.content,
        note: n.expanded || undefined,
        children: n.children.length > 0 ? convert(n.children) : undefined,
      }));
    return convert(outline.nodes);
  }, [outline]);

  // Reset the manual sync override whenever the outline changes so the
  // timestamp comparison takes over again.  This causes the resync bar to
  // reappear when the user edits the outline after importing.
  const outlineRevRef = useRef(outline);
  useEffect(() => {
    if (outlineRevRef.current !== null && outline !== outlineRevRef.current) {
      setOutlineSyncedManual(null);
    }
    outlineRevRef.current = outline;
  }, [outline]);

  // Reset the manual stream sync override whenever the session changes so the
  // timestamp comparison takes over again.  This causes the resync bar to
  // reappear when the user adds new thoughts after importing.
  //
  // We track `session.updated_at` rather than the session object reference so
  // the reset fires even when the session is re-fetched with a newer timestamp
  // (e.g. after the user navigates back from the Stream tab).
  const sessionUpdatedAtRef = useRef(session?.updated_at ?? null);
  useEffect(() => {
    const nextTs = session?.updated_at ?? null;
    if (sessionUpdatedAtRef.current !== null && nextTs !== sessionUpdatedAtRef.current) {
      setStreamSyncedManual(null);
    }
    sessionUpdatedAtRef.current = nextTs;
  }, [session?.updated_at]);

  // Include streaming response as a partial message if currently typing
  // IMPORTANT: This useMemo must be called before any early returns to satisfy React's rules of hooks
  const messages: ChatMessage[] = useMemo(() => {
    const baseMessages = session?.messages || [];
    if (isTyping && streamingResponse) {
      return [
        ...baseMessages,
        {
          role: "assistant" as const,
          content: streamingResponse,
          timestamp: new Date().toISOString(),
        },
      ];
    }
    return baseMessages;
  }, [session?.messages, isTyping, streamingResponse]);

  /** Build a set of all known content strings from outline nodes for stream dedup.
   *  Includes sourceStreamContent (provenance), note (full text), and content (title). */
  const buildOutlineContentSet = useCallback((nodes: OutlineNodeData[]): Set<string> => {
    const set = new Set<string>();
    const collect = (items: OutlineNodeData[]) => {
      for (const node of items) {
        // Prefer sourceStreamContent (provenance) for dedup.
        // For legacy nodes without provenance, add both note and content
        // so that messages matching either the title or body are recognised.
        if (node.sourceStreamContent) {
          set.add(node.sourceStreamContent.trim());
        } else {
          if (node.note) set.add(node.note.trim());
          if (node.content) set.add(node.content.trim());
        }
        if (node.children) collect(node.children);
      }
    };
    collect(nodes);
    return set;
  }, []);

  const isStreamMessageConsumed = useCallback(
    (content: string, outlineContentSet: Set<string>): boolean => {
      if (outlineContentSet.has(content)) return true;
      // If the draft already contains this exact text, treat it as consumed.
      // This covers the case where "Start Draft" seeds the same thought into
      // both the stream and the draft document.
      if (documentContent && documentContent.trim().includes(content)) return true;
      return false;
    },
    [documentContent],
  );

  // Core logic for importing stream messages into outline nodes.
  // `mode` controls whether existing outline nodes are kept ("append") or
  // replaced ("replace"). Default is "append" for backward compatibility.
  const executeStreamToOutlineImport = useCallback(
    (mode: "append" | "replace" = "append") => {
      setShowOutlineSyncConfirm(false);

      if (!messages || messages.length === 0) {
        toast.error("No stream content available. Add some ideas to the Stream first!", {
          id: "import-no-content",
        });
        return;
      }

      const userMessages = messages.filter(
        (m: ChatMessage) => m.role === "user" && m.content.trim().length > 0,
      );

      if (userMessages.length === 0) {
        toast.error("No stream content to import.");
        return;
      }

      // Build set of existing outline content for deduplication.
      // Uses sourceStreamContent (provenance tracking) so edits to outline nodes
      // don't cause the original stream item to be re-imported.
      // Read from ref to avoid stale closure issues (fixes #1454).
      const currentOutline = outlineRef.current;
      const existingNodes = currentOutline ? convertToOutlineNodeData(currentOutline.nodes) : [];
      const existingContentSet =
        mode === "replace" ? new Set<string>() : buildOutlineContentSet(existingNodes);

      // Short items (≤ this length) become the title directly; longer items
      // use a generic numbered title and store full text in the note/body.
      // This avoids the "truncated prefix as title + full text as note"
      // duplication that makes long stream items feel redundant (#1534).
      const SHORT_TITLE_THRESHOLD = 60;

      const newNodes: OutlineNodeData[] = [];
      let streamItemIndex = 0;
      for (const msg of userMessages) {
        const full = msg.content.trim();
        streamItemIndex++;

        // Skip if this message was already consumed — either imported into the
        // outline previously or already present in the draft (e.g. seeded via
        // "Start Draft" quick-start). Fixes #1253.
        if (isStreamMessageConsumed(full, existingContentSet)) {
          continue;
        }

        const isMultiLine = full.includes("\n");
        const firstLine = full.split(/\n/)[0].trim();
        const isLongContent = firstLine.length > SHORT_TITLE_THRESHOLD;

        let title: string;
        let needsNote: boolean;

        if (!isLongContent && !isMultiLine) {
          // Short single-line message — use it directly as the title
          title = firstLine;
          needsNote = false;
        } else {
          // Long or multi-line content: extract a meaningful title from the
          // first sentence/clause and store the full text in the note body.
          // extractTitle() returns the first sentence or first N words,
          // giving a descriptive title instead of a generic "Stream note N".
          const extracted = extractTitle(full, { maxLength: 60, maxWords: 8 });
          title = extracted === "Untitled" ? `Stream note ${streamItemIndex}` : extracted;
          needsNote = true;
        }

        newNodes.push({
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          content: title,
          note: needsNote ? full : undefined,
          isAiGenerated: false,
          sourceStreamContent: full,
          nodeType: "content",
        });
      }

      if (newNodes.length === 0) {
        toast("All stream content has already been imported.", { icon: "ℹ️" });
        return;
      }

      let mergedNodes: OutlineNodeData[];
      if (mode === "replace") {
        mergedNodes = newNodes;
      } else {
        // Remove trailing empty nodes so synced items don't appear after a blank gap
        const trimmedExisting = [...existingNodes];
        while (
          trimmedExisting.length > 0 &&
          trimmedExisting[trimmedExisting.length - 1].content.trim() === "" &&
          !trimmedExisting[trimmedExisting.length - 1].note &&
          !trimmedExisting[trimmedExisting.length - 1].sourceStreamContent &&
          (!trimmedExisting[trimmedExisting.length - 1].children ||
            trimmedExisting[trimmedExisting.length - 1].children!.length === 0)
        ) {
          trimmedExisting.pop();
        }
        mergedNodes = [...trimmedExisting, ...newNodes];
      }

      handleOutlineChange(mergedNodes);
      toast.success(
        `Imported ${newNodes.length} new stream ${newNodes.length === 1 ? "item" : "items"} into outline`,
      );
    },
    [
      messages,
      convertToOutlineNodeData,
      handleOutlineChange,
      buildOutlineContentSet,
      isStreamMessageConsumed,
    ],
  );

  // Public handler: shows confirmation dialog when outline already has content,
  // otherwise imports directly. Fixes #1474.
  const handleImportStreamToOutline = useCallback(() => {
    if (!messages || messages.length === 0) {
      toast.error("No stream content available. Add some ideas to the Stream first!", {
        id: "import-no-content",
      });
      return;
    }

    const userMessages = messages.filter(
      (m: ChatMessage) => m.role === "user" && m.content.trim().length > 0,
    );
    if (userMessages.length === 0) {
      toast.error("No stream content to import.");
      return;
    }

    // Check if outline already has meaningful content
    const currentOutline = outlineRef.current;
    const existingNodes = currentOutline ? convertToOutlineNodeData(currentOutline.nodes) : [];
    const hasExistingContent = existingNodes.some(
      (n) => n.content.trim().length > 0 || !!n.note || (n.children && n.children.length > 0),
    );

    if (hasExistingContent) {
      setShowOutlineSyncConfirm(true);
    } else {
      executeStreamToOutlineImport("append");
    }
  }, [messages, convertToOutlineNodeData, executeStreamToOutlineImport]);

  // Check whether a stream message's content has already been consumed by
  // the outline OR the draft. This prevents the "Sync Stream" badge from
  // offering to re-import content that was already seeded into the draft
  // when the project was started in draft mode. Fixes #1253.
  // Whether all stream user messages have already been imported into the outline
  const streamToOutlineSynced = useMemo(() => {
    if (!messages || messages.length === 0) return false;
    const userMessages = messages.filter(
      (m: ChatMessage) => m.role === "user" && m.content.trim().length > 0,
    );
    if (userMessages.length === 0) return false;
    const existingNodes = outline ? convertToOutlineNodeData(outline.nodes) : [];
    const existingContentSet = buildOutlineContentSet(existingNodes);
    return userMessages.every((msg: ChatMessage) =>
      isStreamMessageConsumed(msg.content.trim(), existingContentSet),
    );
  }, [
    messages,
    outline,
    convertToOutlineNodeData,
    buildOutlineContentSet,
    isStreamMessageConsumed,
  ]);

  // Count of stream user messages not yet imported into the outline
  const unsyncedStreamCount = useMemo(() => {
    if (!messages || messages.length === 0) return 0;
    const userMessages = messages.filter(
      (m: ChatMessage) => m.role === "user" && m.content.trim().length > 0,
    );
    if (userMessages.length === 0) return 0;
    const existingNodes = outline ? convertToOutlineNodeData(outline.nodes) : [];
    const existingContentSet = buildOutlineContentSet(existingNodes);
    return userMessages.filter(
      (msg: ChatMessage) => !isStreamMessageConsumed(msg.content.trim(), existingContentSet),
    ).length;
  }, [
    messages,
    outline,
    convertToOutlineNodeData,
    buildOutlineContentSet,
    isStreamMessageConsumed,
  ]);

  // Compute word count before early returns
  const wordCount = getWordCount();

  if (loading) {
    return (
      <>
        <Header />
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </>
    );
  }

  if (error || !project) {
    return (
      <>
        <Header />
        <div className={styles.errorState}>
          <h1>{t("projectDetail.projectNotFound")}</h1>
          <p>{error || t("projectDetail.projectNotFoundDescription")}</p>
          <Link to="/dashboard">
            <ArrowLeftIcon />
            {t("projectDetail.backToDashboard")}
          </Link>
        </div>
      </>
    );
  }

  const currentMode = project.current_mode;

  const handleSettingsSave = async (newTitle: string) => {
    if (!orgId || !projectId) return;
    setSettingsSaving(true);
    try {
      const updated = await projectAPI.update(orgId, projectId, {
        title: newTitle,
      });
      setProject(updated);
      toast.success("Project renamed");
    } catch {
      toast.error("Failed to rename project");
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleSettingsDelete = async () => {
    if (!orgId || !projectId) return;
    setSettingsDeleting(true);
    try {
      await projectAPI.delete(orgId, projectId);
      toast.success("Project deleted");
      navigate("/dashboard");
    } catch {
      toast.error("Failed to delete project");
      setSettingsDeleting(false);
    }
  };

  return (
    <>
      <div className={`${styles.page} ${isFullscreen ? styles.zenActive : ""}`}>
        {!isFullscreen && <Header />}
        {isFullscreen && (
          <>
            <button
              type="button"
              className={styles.zenExitButton}
              onClick={exitFullscreen}
              title="Exit Zen Mode (Ctrl+Shift+Z) · Switch views: Alt+1/2/3"
            >
              ✕
            </button>
            <div className={styles.zenTitle}>{project.title}</div>
            <div className={styles.zenWordCount}>
              {wordCount.toLocaleString()} {t("projectDetail.words")}
            </div>
          </>
        )}
        <div className={`${styles.topBar} ${isFullscreen ? styles.hidden : ""}`}>
          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <Link to="/dashboard" className={styles.breadcrumbLink}>
              {t("navigation.dashboard")}
            </Link>
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <EditableTitle
              value={project.title}
              onSave={async (newTitle) => {
                if (!orgId || !projectId) return;
                try {
                  const updated = await projectAPI.update(orgId, projectId, { title: newTitle });
                  setProject(updated);
                } catch {
                  toast.error("Failed to rename project");
                }
              }}
            />
          </nav>
          <div className={styles.topBarActions}>
            {aiConfigured !== true && !isFullscreen && currentMode !== "edit" && (
              <AIStatusBanner onClick={() => navigateToAISettings()} />
            )}
            {currentMode === "edit" && document && orgId && (
              <>
                <ExportDropdown
                  orgId={orgId}
                  documentId={document.id}
                  documentTitle={project.title}
                  onExportComplete={(format) =>
                    toast.success(`Downloaded as ${FORMAT_DISPLAY_NAMES[format]}`)
                  }
                  onExportError={(error) => toast.error(error)}
                />
                <button
                  type="button"
                  className={`${styles.iconButton} ${showVersionHistory ? styles.iconButtonActive : ""}`}
                  title={t("projectDetail.versionHistory")}
                  onClick={() => setShowVersionHistory(!showVersionHistory)}
                >
                  <HistoryIcon />
                </button>
              </>
            )}
            <ZenModeToggle />
            <button
              type="button"
              className={`${styles.iconButton} ${showSettings ? styles.iconButtonActive : ""}`}
              title={t("settings.title")}
              onClick={() => openSettings()}
            >
              <SettingsIcon />
            </button>
          </div>
          {/* Mobile overflow menu */}
          <div className={styles.mobileMenuWrapper}>
            <button
              type="button"
              className={styles.mobileMenuButton}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t("projectDetail.moreActions")}
            >
              <MoreIcon />
            </button>
            {mobileMenuOpen && (
              <>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  className={styles.mobileMenuBackdrop}
                  onClick={() => setMobileMenuOpen(false)}
                />
                <div className={styles.mobileMenu}>
                  {aiConfigured !== true && currentMode !== "edit" && (
                    <div className={styles.mobileMenuItem}>
                      <AIStatusBanner onClick={() => navigateToAISettings()} />
                    </div>
                  )}
                  {currentMode === "edit" && document && orgId && (
                    <>
                      <button
                        type="button"
                        className={styles.mobileMenuItem}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          // Export handled by dropdown — for mobile, just show version history toggle
                        }}
                      >
                        <ExportDropdown
                          orgId={orgId}
                          documentId={document.id}
                          documentTitle={project.title}
                          onExportComplete={(format) => {
                            setMobileMenuOpen(false);
                            toast.success(`Downloaded as ${FORMAT_DISPLAY_NAMES[format]}`);
                          }}
                          onExportError={(error) => toast.error(error)}
                        />
                      </button>
                      <button
                        type="button"
                        className={styles.mobileMenuItem}
                        onClick={() => {
                          setShowVersionHistory(!showVersionHistory);
                          setMobileMenuOpen(false);
                        }}
                      >
                        <HistoryIcon /> {t("projectDetail.versionHistory")}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className={styles.mobileMenuItem}
                    onClick={() => {
                      openSettings();
                      setMobileMenuOpen(false);
                    }}
                  >
                    <SettingsIcon /> {t("settings.title")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={`${styles.modeBar} ${isFullscreen ? styles.hidden : ""}`}>
          <div className={styles.modeSwitcher} role="tablist" aria-label="Project views">
            {(Object.keys(modeI18nKeys) as ProjectMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                role="tab"
                id={`tab-${mode}`}
                aria-selected={currentMode === mode}
                aria-controls={`tabpanel-${mode}`}
                className={`${styles.modeButton} ${currentMode === mode ? styles.modeButtonActive : ""}`}
                data-mode={mode}
                onClick={() => handleModeChange(mode)}
                disabled={updating}
              >
                {mode === "stream" && <StreamIcon />}
                {mode === "outline" && <OutlineIcon />}
                {mode === "edit" && <EditIcon />}
                {t(modeI18nKeys[mode].label)}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.main}>
          <div className={styles.editorArea}>
            <div
              className={styles.editorContainer}
              role="tabpanel"
              id={`tabpanel-${currentMode}`}
              aria-labelledby={`tab-${currentMode}`}
            >
              {currentMode === "stream" && (
                <StreamChat
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  onEditMessage={handleEditMessage}
                  onDeleteMessage={handleDeleteMessage}
                  onTransitionToOutline={handleTransitionToOutline}
                  onNavigateToSettings={() => navigateToAISettings()}
                  onNavigateToOutline={() => handleModeChange("outline")}
                  outlineItems={streamOutlineItems}
                  isStreamSynced={streamToOutlineSynced}
                  unsyncedStreamCount={unsyncedStreamCount}
                  onImportStreamToOutline={handleImportStreamToOutline}
                  isLoading={sessionLoading}
                  isTyping={isTyping}
                  showOutlinePrompt={showOutlinePrompt}
                  isGeneratingOutline={outlineGenerating}
                  isZenMode={isFullscreen}
                  aiConfigured={aiConfigured === true}
                  aiEnabled={aiEnabled}
                  wordCount={wordCount}
                  lastSaved={latestSavedTimestamp}
                />
              )}
              {currentMode === "outline" && (
                <div className={styles.outlineContainer}>
                  {outlineLoading || outlineGenerating ? (
                    <div className={styles.modePlaceholder}>
                      <div className={styles.spinner} />
                      {outlineGenerating && <p>{t("projectDetail.generatingOutline")}</p>}
                    </div>
                  ) : (
                    <OutlineEditor
                      nodes={outline ? convertToOutlineNodeData(outline.nodes) : []}
                      onChange={handleOutlineChange}
                      onDeleteNode={handleDeleteOutlineNode}
                      onExpandPoint={aiConfigured === true ? handleExpandPoint : undefined}
                      onGenerateFromStream={handleTransitionToOutline}
                      onImportFromStream={handleImportStreamToOutline}
                      onNavigateToSettings={() => navigateToAISettings()}
                      isGenerating={outlineGenerating}
                      aiConfigured={aiConfigured}
                      aiEnabled={aiEnabled}
                      isZenMode={isFullscreen}
                      projectId={projectId}
                      streamMessages={messages}
                      isStreamSynced={streamToOutlineSynced}
                      unsyncedStreamCount={unsyncedStreamCount}
                    />
                  )}
                </div>
              )}
              {currentMode === "edit" &&
                (documentLoading ? (
                  <div className={styles.modePlaceholder}>
                    <div className={styles.spinner} />
                  </div>
                ) : document ? (
                  <div
                    className={`${styles.draftWithSidebar} ${draftStreamSidebarOpen ? styles.draftWithSidebarOpen : ""}`}
                  >
                    <div
                      className={`${focusModeEnabled ? styles.focusModeActive : ""} ${styles.draftEditorArea}`}
                    >
                      {focusModeEnabled && !isFullscreen && (
                        <button
                          type="button"
                          className={styles.focusModeExitButton}
                          onClick={() => setFocusModeEnabled(false)}
                          title={t("editor.exitFocusMode", "Exit Focus Mode (Ctrl+Shift+F)")}
                        >
                          {t("editor.exitFocusMode", "Exit Focus")}
                        </button>
                      )}
                      {documentContent.trim().length === 0 &&
                        !isFullscreen &&
                        (hasStreamContent || hasOutlineContent) && (
                          <div className={styles.draftImportBar}>
                            <span className={styles.draftImportBarLabel}>
                              {t("projectDetail.draftEmptyImportLabel")}
                            </span>
                            <div className={styles.draftBannerActions}>
                              {aiConfigured === true && hasStreamContent && (
                                <button
                                  type="button"
                                  className={styles.importFromStreamButton}
                                  onClick={handleGenerateDraft}
                                  disabled={generatingDraft || importingStream || importingOutline}
                                >
                                  {generatingDraft ? (
                                    <>
                                      <span className={styles.spinner} />{" "}
                                      {t("projectDetail.generating")}
                                    </>
                                  ) : (
                                    t("projectDetail.aiDraft")
                                  )}
                                </button>
                              )}
                              {hasStreamContent && (
                                <button
                                  type="button"
                                  className={styles.importFromStreamButton}
                                  onClick={() => handleImportFromStream()}
                                  disabled={importingStream || generatingDraft || importingOutline}
                                >
                                  {importingStream ? (
                                    <>
                                      <span className={styles.spinner} />{" "}
                                      {t("projectDetail.importing")}
                                    </>
                                  ) : streamSynced && documentContent.trim().length > 0 ? (
                                    t("projectDetail.streamImportedHint")
                                  ) : (
                                    t("projectDetail.importFromStream")
                                  )}
                                </button>
                              )}
                              {hasOutlineContent && (
                                <button
                                  type="button"
                                  className={styles.importFromStreamButton}
                                  onClick={requestImportFromOutline}
                                  disabled={importingOutline || importingStream || generatingDraft}
                                >
                                  {importingOutline ? (
                                    <>
                                      <span className={styles.spinner} />{" "}
                                      {t("projectDetail.importingOutline")}
                                    </>
                                  ) : outlineSynced && documentContent.trim().length > 0 ? (
                                    t("projectDetail.outlineImportedHint")
                                  ) : (
                                    t("projectDetail.importFromOutline")
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      {hasOutlineContent &&
                        !outlineSynced &&
                        !isFullscreen &&
                        documentContent.trim().length > 0 && (
                          <div className={styles.draftResyncBar}>
                            <span>{t("projectDetail.outlineUpdatedHint")}</span>
                            <button
                              type="button"
                              className={styles.resyncButton}
                              onClick={() => requestImportFromOutline()}
                              disabled={importingOutline || generatingDraft || importingStream}
                            >
                              {importingOutline
                                ? t("projectDetail.importing", "Importing…")
                                : t("projectDetail.updateDraft", "Update Draft")}
                            </button>
                          </div>
                        )}
                      {hasStreamContent &&
                        !streamSynced &&
                        !isFullscreen &&
                        documentContent.trim().length > 0 && (
                          <div className={styles.draftResyncBar}>
                            <span>{t("projectDetail.streamUpdatedHint")}</span>
                            <button
                              type="button"
                              className={styles.resyncButton}
                              onClick={() => handleImportFromStream("append")}
                              disabled={importingStream || generatingDraft || importingOutline}
                            >
                              {importingStream
                                ? t("projectDetail.importing")
                                : t("projectDetail.importFromStream")}
                            </button>
                          </div>
                        )}
                      <RichTextEditor
                        key={`${document.id}-${editorKey}`}
                        initialContent={document.content as SerializedEditorState | null}
                        suggestions={focusModeEnabled ? [] : editorSuggestions}
                        onAcceptSuggestion={handleAcceptSuggestion}
                        onRejectSuggestion={handleRejectSuggestion}
                        onChange={handleEditorChange}
                        onAutoSave={handleAutoSave}
                        placeholder={t("projectDetail.startWritingDraft")}
                        autoSaveInterval={3000}
                      />
                    </div>
                    {hasStreamContent && !isFullscreen && (
                      <button
                        type="button"
                        className={`${styles.draftStreamToggle} ${draftStreamSidebarOpen ? styles.draftStreamToggleActive : ""}`}
                        onClick={() => setDraftStreamSidebarOpen(!draftStreamSidebarOpen)}
                        title={
                          draftStreamSidebarOpen
                            ? t("projectDetail.hideStreamPanel")
                            : t("projectDetail.showStreamPanel")
                        }
                        aria-pressed={draftStreamSidebarOpen}
                      >
                        <StreamIcon />
                      </button>
                    )}
                    {draftStreamSidebarOpen && hasStreamContent && !isFullscreen && (
                      <StreamSidebar
                        messages={messages}
                        onClose={() => setDraftStreamSidebarOpen(false)}
                        context="draft"
                      />
                    )}
                  </div>
                ) : (
                  <div className={styles.modePlaceholder}>
                    <h3>{t("projectDetail.noDraftYet")}</h3>
                    <p>
                      {hasStreamContent && hasOutlineContent
                        ? t("projectDetail.noDraftWithStreamAndOutline")
                        : hasOutlineContent
                          ? t("projectDetail.noDraftWithOutline")
                          : hasStreamContent
                            ? t("projectDetail.noDraftWithStream")
                            : t("projectDetail.noDraftWithoutStream")}
                    </p>
                    <div className={styles.draftActions}>
                      {hasStreamContent || hasOutlineContent ? (
                        <>
                          {hasStreamContent && (
                            <button
                              type="button"
                              className={styles.generateDraftButton}
                              onClick={() => handleImportFromStream()}
                              disabled={importingStream || generatingDraft || importingOutline}
                            >
                              {importingStream ? (
                                <>
                                  <span className={styles.spinner} /> {t("projectDetail.importing")}
                                </>
                              ) : streamSynced && documentContent.trim().length > 0 ? (
                                t("projectDetail.streamImportedHint")
                              ) : (
                                t("projectDetail.importFromStream")
                              )}
                            </button>
                          )}
                          {hasOutlineContent && (
                            <button
                              type="button"
                              className={styles.generateDraftButton}
                              onClick={requestImportFromOutline}
                              disabled={importingOutline || importingStream || generatingDraft}
                            >
                              {importingOutline ? (
                                <>
                                  <span className={styles.spinner} />{" "}
                                  {t("projectDetail.importingOutline")}
                                </>
                              ) : outlineSynced && documentContent.trim().length > 0 ? (
                                t("projectDetail.outlineImportedHint")
                              ) : (
                                t("projectDetail.importFromOutline")
                              )}
                            </button>
                          )}
                          {aiConfigured === true && hasStreamContent && (
                            <button
                              type="button"
                              className={styles.generateDraftButton}
                              onClick={handleGenerateDraft}
                              disabled={generatingDraft || importingStream || importingOutline}
                            >
                              {generatingDraft ? (
                                <>
                                  <span className={styles.spinner} />{" "}
                                  {t("projectDetail.generating")}
                                </>
                              ) : (
                                t("projectDetail.aiDraftFromStream")
                              )}
                            </button>
                          )}
                        </>
                      ) : (
                        <button
                          type="button"
                          className={styles.generateDraftButton}
                          onClick={() => handleModeChange("stream")}
                        >
                          {t("projectDetail.goToStream")}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Mobile sidebar toggle — hidden in Stream mode (stats consolidated in header) and Focus mode (sidebar collapsed) */}
          {!isFullscreen &&
            currentMode !== "stream" &&
            !(currentMode === "edit" && focusModeEnabled) && (
              <button
                type="button"
                className={styles.mobileSidebarToggle}
                onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              >
                <InfoIcon />{" "}
                {mobileSidebarOpen ? t("projectDetail.hideInfo") : t("projectDetail.showInfo")}
              </button>
            )}
          <aside
            className={`${styles.sidebar} ${isFullscreen || currentMode === "stream" || (currentMode === "edit" && focusModeEnabled) ? styles.hidden : ""} ${mobileSidebarOpen ? styles.sidebarOpen : ""}`}
          >
            <section className={styles.sidebarSection}>
              <h3 className={styles.sidebarTitle}>
                {currentMode === "stream"
                  ? t("projectDetail.streamInfo")
                  : currentMode === "outline"
                    ? t("projectDetail.outlineInfo")
                    : currentMode === "edit"
                      ? t("projectDetail.draftInfo")
                      : t("projectDetail.documentInfo")}
              </h3>
              <ul className={styles.metaList}>
                <li className={styles.metaItem}>
                  <span className={styles.metaLabel}>{t("projectDetail.words")}</span>
                  <span className={styles.metaValue}>{wordCount}</span>
                </li>
                <li className={styles.metaItem}>
                  <span className={styles.metaLabel}>{t("projectDetail.readingTime")}</span>
                  <span className={styles.metaValue}>
                    {wordCount < 200 ? "< 1" : Math.ceil(wordCount / 200)} {t("projectDetail.min")}
                  </span>
                </li>
                <li className={styles.metaItem}>
                  <span className={styles.metaLabel}>{t("projectDetail.lastSaved")}</span>
                  <span className={styles.metaValue}>
                    {latestSavedTimestamp
                      ? new Date(latestSavedTimestamp).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : "—"}
                  </span>
                </li>
              </ul>
            </section>

            {currentMode === "stream" && session && (
              <section className={styles.sidebarSection}>
                <h3 className={styles.sidebarTitle}>{t("projectDetail.session")}</h3>
                <ul className={styles.metaList}>
                  <li className={styles.metaItem}>
                    <span className={styles.metaLabel}>{t("stream.messages")}</span>
                    <span className={styles.metaValue}>{messages.length}</span>
                  </li>
                </ul>
              </section>
            )}

            {currentMode === "edit" && (
              <>
                <section className={styles.sidebarSection}>
                  <h3 className={styles.sidebarTitle}>{t("projectDetail.writingAids")}</h3>
                  <div className={styles.writingModeControls}>
                    <FocusModeToggle enabled={focusModeEnabled} onToggle={setFocusModeEnabled} />
                    <RealTimeSuggestionIndicator
                      isGenerating={isGenerating}
                      enabled={realTimeEnabled}
                      onToggle={setRealTimeEnabled}
                      lastGeneratedAt={lastGeneratedAt}
                      disabled={aiConfigured !== true}
                    />
                  </div>
                  {focusModeEnabled && (
                    <p className={styles.focusModeHint}>
                      Suggestions hidden. Press <kbd>Cmd/Ctrl+Shift+F</kbd> to exit.
                    </p>
                  )}
                  {realTimeError && aiConfigured === true && !focusModeEnabled && (
                    <p className={styles.errorHint}>{realTimeError}</p>
                  )}
                  {aiConfigured !== true && !focusModeEnabled && (
                    <div className={styles.aiSetupCta}>
                      <p className={styles.infoHint}>
                        {t("projectDetail.connectAIForSuggestions")}
                      </p>
                      <button
                        type="button"
                        className={styles.connectAIButton}
                        onClick={() => navigateToAISettings()}
                      >
                        {t("projectDetail.connectAI")}
                      </button>
                    </div>
                  )}
                </section>
                {!focusModeEnabled && (
                  <section className={styles.sidebarSection}>
                    <SuggestionControls
                      suggestions={suggestions.map((s) => ({
                        id: s.id,
                        textRange: {
                          start: s.text_range_start,
                          end: s.text_range_end,
                        },
                        originalText: s.original_text,
                        suggestedText: s.suggested_text,
                        category: s.category,
                        confidenceScore: s.confidence_score,
                        explanation: s.explanation ?? undefined,
                      }))}
                      activeFilters={activeFilters}
                      onFilterChange={setActiveFilters}
                      onAcceptAll={handleAcceptAll}
                      onRejectAll={handleRejectAll}
                    />
                  </section>
                )}
              </>
            )}

            {currentMode === "edit" &&
              (hasStreamContent || hasOutlineContent) &&
              documentContent.trim().length > 0 && (
                <section className={styles.sidebarSection}>
                  <h3 className={styles.sidebarTitle}>{t("projectDetail.contentSources")}</h3>
                  <div className={styles.contentSourcesList}>
                    {hasStreamContent && (
                      <button
                        type="button"
                        className={`${styles.contentSourceButton} ${!streamSynced ? styles.contentSourceUpdated : ""}`}
                        onClick={requestImportFromStream}
                        disabled={importingStream || generatingDraft || importingOutline}
                        title={
                          streamSynced
                            ? t(
                                "projectDetail.streamSyncedTooltip",
                                "Stream content is already imported",
                              )
                            : undefined
                        }
                      >
                        <StreamIcon />
                        <span className={styles.contentSourceLabel}>
                          {importingStream
                            ? t("projectDetail.importing")
                            : streamSynced
                              ? t("projectDetail.streamImportedHint")
                              : t("projectDetail.importFromStream")}
                        </span>
                        {!streamSynced && (
                          <span className={styles.contentSourceBadge}>
                            {t("projectDetail.updated")}
                          </span>
                        )}
                        {importingStream && <span className={styles.spinner} />}
                      </button>
                    )}
                    {hasOutlineContent && (
                      <button
                        type="button"
                        className={`${styles.contentSourceButton} ${!outlineSynced ? styles.contentSourceUpdated : ""}`}
                        onClick={requestImportFromOutline}
                        disabled={importingOutline || importingStream || generatingDraft}
                        title={
                          outlineSynced
                            ? t(
                                "projectDetail.outlineSyncedTooltip",
                                "Outline content is already imported",
                              )
                            : undefined
                        }
                      >
                        <OutlineIcon />
                        <span className={styles.contentSourceLabel}>
                          {importingOutline
                            ? t("projectDetail.importingOutline")
                            : outlineSynced
                              ? t("projectDetail.outlineImportedHint")
                              : t("projectDetail.importFromOutline")}
                        </span>
                        {!outlineSynced && (
                          <span className={styles.contentSourceBadge}>
                            {t("projectDetail.updated")}
                          </span>
                        )}
                        {importingOutline && <span className={styles.spinner} />}
                      </button>
                    )}
                    {aiConfigured === true && hasStreamContent && (
                      <button
                        type="button"
                        className={styles.contentSourceButton}
                        onClick={handleGenerateDraft}
                        disabled={generatingDraft || importingStream || importingOutline}
                      >
                        <span className={styles.contentSourceLabel}>
                          {generatingDraft
                            ? t("projectDetail.generating")
                            : t("projectDetail.aiDraft")}
                        </span>
                        {generatingDraft && <span className={styles.spinner} />}
                      </button>
                    )}
                  </div>
                </section>
              )}
          </aside>

          {showVersionHistory && currentMode === "edit" && document && orgId && (
            <aside className={styles.versionSidebar}>
              <VersionHistorySidebar
                orgId={orgId}
                documentId={document.id}
                onRollback={(doc) => {
                  setDocument(doc as DocumentResponse);
                  loadDocument();
                  toast.success("Version restored successfully");
                }}
                onClose={() => setShowVersionHistory(false)}
              />
            </aside>
          )}
        </div>

        {showSettings && (
          <ProjectSettingsModal
            title={project.title}
            createdAt={project.created_at}
            updatedAt={project.updated_at}
            saving={settingsSaving}
            deleting={settingsDeleting}
            aiConfigured={rawAiConfigured}
            aiEnabled={aiEnabled}
            aiNeedsVerification={aiNeedsVerification}
            onSave={handleSettingsSave}
            onDelete={handleSettingsDelete}
            onClose={closeSettings}
            onConfigured={() => setAiConfiguredOverride(true)}
            onNavigateToAISettings={navigateToAISettings}
          />
        )}
        {showStreamReimportConfirm && (
          <Modal
            onClose={() => setShowStreamReimportConfirm(false)}
            labelledBy="stream-reimport-dialog-title"
          >
            <h2 id="stream-reimport-dialog-title" className={styles.reimportTitle}>
              {t("projectDetail.streamReimportConfirmTitle", "Import stream to draft")}
            </h2>
            <p className={styles.reimportMessage}>
              {t(
                "projectDetail.streamReimportConfirmMessage",
                "Your draft already has content. Would you like to append the stream content to the end or replace the entire draft?",
              )}
            </p>
            <div className={styles.reimportActions}>
              <button
                type="button"
                className={styles.reimportCancel}
                onClick={() => setShowStreamReimportConfirm(false)}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className={styles.reimportAppend}
                onClick={() => handleImportFromStream("append")}
              >
                {t("projectDetail.appendToDraft", "Append to draft")}
              </button>
              <button
                type="button"
                className={styles.reimportReplace}
                onClick={() => handleImportFromStream("replace")}
              >
                {t("projectDetail.streamReimportConfirmAction", "Replace draft")}
              </button>
            </div>
          </Modal>
        )}
        {showOutlineSyncConfirm && (
          <Modal
            onClose={() => setShowOutlineSyncConfirm(false)}
            labelledBy="outline-sync-dialog-title"
          >
            <h2 id="outline-sync-dialog-title" className={styles.reimportTitle}>
              {t("projectDetail.outlineSyncConfirmTitle", "Sync stream to outline")}
            </h2>
            <p className={styles.reimportMessage}>
              {t(
                "projectDetail.outlineSyncConfirmMessage",
                "Your outline already has content. Would you like to append the stream items or replace the entire outline?",
              )}
            </p>
            <div className={styles.reimportActions}>
              <button
                type="button"
                className={styles.reimportCancel}
                onClick={() => setShowOutlineSyncConfirm(false)}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className={styles.reimportAppend}
                onClick={() => executeStreamToOutlineImport("append")}
              >
                {t("projectDetail.appendToOutline", "Append to outline")}
              </button>
              <button
                type="button"
                className={styles.reimportReplace}
                onClick={() => executeStreamToOutlineImport("replace")}
              >
                {t("projectDetail.replaceOutline", "Replace outline")}
              </button>
            </div>
          </Modal>
        )}
        {showReimportConfirm && (
          <Modal onClose={() => setShowReimportConfirm(false)} labelledBy="reimport-dialog-title">
            <h2 id="reimport-dialog-title" className={styles.reimportTitle}>
              {t("projectDetail.reimportConfirmTitle", "Import outline to draft")}
            </h2>
            <p className={styles.reimportMessage}>
              {t(
                "projectDetail.reimportConfirmMessage",
                "Your draft already has content. Would you like to append the outline to the end or replace the entire draft?",
              )}
            </p>
            <div className={styles.reimportActions}>
              <button
                type="button"
                className={styles.reimportCancel}
                onClick={() => setShowReimportConfirm(false)}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className={styles.reimportAppend}
                onClick={() => handleImportFromOutline("append")}
              >
                {t("projectDetail.appendToDraft", "Append to draft")}
              </button>
              <button
                type="button"
                className={styles.reimportReplace}
                onClick={() => handleImportFromOutline("replace")}
              >
                {t("projectDetail.reimportConfirmAction", "Replace draft")}
              </button>
            </div>
          </Modal>
        )}
        {showAISetupModal && (
          <AISetupModal
            onClose={() => setShowAISetupModal(false)}
            onConfigured={() => {
              setShowAISetupModal(false);
              setAiConfigured(true);
              refreshAIStatus();
            }}
          />
        )}
      </div>
    </>
  );
}

function EditableTitle({
  value,
  onSave,
}: {
  value: string;
  onSave: (newTitle: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const committingRef = useRef(false);

  // Sync external value changes (but not while a save is in flight)
  useEffect(() => {
    if (!editing && !saving) setDraft(value);
  }, [value, editing, saving]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = async () => {
    // Guard against double-fire (blur + Enter can race)
    if (committingRef.current) return;
    committingRef.current = true;

    const trimmed = draft.trim();
    setEditing(false);

    if (!trimmed || trimmed === value) {
      setDraft(value);
      committingRef.current = false;
      return;
    }

    // Keep the optimistic draft visible while saving
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
      committingRef.current = false;
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={`${styles.projectTitle} ${styles.projectTitleInput}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") {
            setDraft(value);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <h1
      className={styles.projectTitle}
      title={`${value} — click to rename`}
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") setEditing(true);
      }}
    >
      {value}
      <PencilIcon />
    </h1>
  );
}

function PencilIcon() {
  return (
    <svg
      className={styles.editIcon}
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.5 1.5L14.5 4.5L5 14H2V11L11.5 1.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 12L6 8L10 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.1667 12.5C16.0557 12.7513 16.0226 13.0302 16.0716 13.3005C16.1207 13.5708 16.2495 13.8203 16.4417 14.0167L16.4917 14.0667C16.6466 14.2215 16.7695 14.4053 16.8534 14.6076C16.9373 14.8099 16.9805 15.0268 16.9805 15.2458C16.9805 15.4649 16.9373 15.6817 16.8534 15.8841C16.7695 16.0864 16.6466 16.2702 16.4917 16.425C16.3369 16.5799 16.1531 16.7028 15.9508 16.7867C15.7484 16.8706 15.5316 16.9138 15.3125 16.9138C15.0935 16.9138 14.8766 16.8706 14.6743 16.7867C14.4719 16.7028 14.2882 16.5799 14.1333 16.425L14.0833 16.375C13.887 16.1828 13.6375 16.054 13.3672 16.005C13.0969 15.9559 12.818 15.989 12.5667 16.1C12.3203 16.2056 12.1124 16.381 11.9671 16.6046C11.8218 16.8282 11.7456 17.0902 11.75 17.3583V17.5C11.75 17.942 11.5744 18.366 11.2624 18.6785C10.9504 18.9911 10.5264 19.1667 10.0833 19.1667C9.64131 19.1667 9.21738 18.9911 8.90482 18.6785C8.59226 18.366 8.41667 17.942 8.41667 17.5V17.4167C8.41567 17.1424 8.32839 16.8756 8.16758 16.6532C8.00677 16.4308 7.78053 16.2639 7.51667 16.175C7.26533 16.064 6.98646 16.0309 6.71616 16.0799C6.44587 16.129 6.19639 16.2578 6 16.45L5.95 16.5C5.79516 16.6549 5.61138 16.7778 5.40905 16.8617C5.20672 16.9456 4.98985 16.9888 4.77083 16.9888C4.55182 16.9888 4.33495 16.9456 4.13262 16.8617C3.93028 16.7778 3.74651 16.6549 3.59167 16.5C3.43681 16.3452 3.3139 16.1614 3.22999 15.9591C3.14609 15.7567 3.10287 15.5399 3.10287 15.3208C3.10287 15.1018 3.14609 14.8849 3.22999 14.6826C3.3139 14.4803 3.43681 14.2965 3.59167 14.1417L3.64167 14.0917C3.83388 13.8953 3.96267 13.6458 4.01169 13.3755C4.06071 13.1052 4.02765 12.8264 3.91667 12.575C3.81101 12.3286 3.63567 12.1207 3.41206 11.9754C3.18844 11.8301 2.92648 11.7539 2.65833 11.7583H2.5C2.05797 11.7583 1.63405 11.5827 1.32149 11.2702C1.00893 10.9576 0.833333 10.5337 0.833333 10.0917C0.833333 9.64964 1.00893 9.22571 1.32149 8.91315C1.63405 8.60059 2.05797 8.425 2.5 8.425H2.58333C2.8576 8.42399 3.12444 8.33671 3.34681 8.1759C3.56918 8.0151 3.73612 7.78885 3.825 7.525C3.93598 7.27366 3.96904 6.99479 3.92002 6.72449C3.871 6.45419 3.74222 6.20472 3.55 6.00833L3.5 5.95833C3.34514 5.80349 3.22223 5.61972 3.13832 5.41738C3.05442 5.21505 3.0112 4.99818 3.0112 4.77917C3.0112 4.56015 3.05442 4.34328 3.13832 4.14095C3.22223 3.93862 3.34514 3.75484 3.5 3.6C3.65484 3.44514 3.83862 3.32223 4.04095 3.23832C4.24328 3.15442 4.46015 3.1112 4.67917 3.1112C4.89818 3.1112 5.11505 3.15442 5.31738 3.23832C5.51972 3.32223 5.70349 3.44514 5.85833 3.6L5.90833 3.65C6.10472 3.84222 6.35419 3.971 6.62449 4.02002C6.89479 4.06904 7.17366 4.03598 7.425 3.925H7.5C7.74639 3.81934 7.95431 3.6439 8.09958 3.42029C8.24484 3.19667 8.32104 2.93471 8.31667 2.66667V2.5C8.31667 2.05797 8.49226 1.63405 8.80482 1.32149C9.11738 1.00893 9.54131 0.833333 9.98333 0.833333C10.4254 0.833333 10.8493 1.00893 11.1618 1.32149C11.4744 1.63405 11.65 2.05797 11.65 2.5V2.58333C11.6456 2.85148 11.7218 3.11344 11.8671 3.33706C12.0124 3.56067 12.2203 3.73601 12.4667 3.84167C12.718 3.95265 12.9969 3.98571 13.2672 3.93669C13.5375 3.88767 13.787 3.75889 13.9833 3.56667L14.0333 3.51667C14.1882 3.36181 14.3719 3.2389 14.5743 3.15499C14.7766 3.07109 14.9935 3.02787 15.2125 3.02787C15.4315 3.02787 15.6484 3.07109 15.8507 3.15499C16.0531 3.2389 16.2368 3.36181 16.3917 3.51667C16.5465 3.67151 16.6695 3.85528 16.7534 4.05762C16.8373 4.25995 16.8805 4.47682 16.8805 4.69583C16.8805 4.91485 16.8373 5.13172 16.7534 5.33405C16.6695 5.53638 16.5465 5.72016 16.3917 5.875L16.3417 5.925C16.1495 6.12139 16.0207 6.37087 15.9716 6.64116C15.9226 6.91146 15.9557 7.19033 16.0667 7.44167V7.5C16.1724 7.74639 16.3478 7.95431 16.5714 8.09958C16.795 8.24484 17.057 8.32104 17.325 8.31667H17.5C17.942 8.31667 18.366 8.49226 18.6785 8.80482C18.9911 9.11738 19.1667 9.54131 19.1667 9.98333C19.1667 10.4254 18.9911 10.8493 18.6785 11.1618C18.366 11.4744 17.942 11.65 17.5 11.65H17.4167C17.1485 11.6544 16.8866 11.7306 16.6629 11.8758C16.4393 12.0211 16.2639 12.229 16.1583 12.475"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StreamIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 2V14M4 6L8 2L12 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OutlineIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 4H14M2 8H10M2 12H14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6667 1.44775C12.9143 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5 13.6667L1.33333 14.6667L2.33333 11L11.3333 2.00004Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="4" r="1.5" fill="currentColor" />
      <circle cx="10" cy="10" r="1.5" fill="currentColor" />
      <circle cx="10" cy="16" r="1.5" fill="currentColor" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="5" r="0.75" fill="currentColor" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 5V10L13.5 13.5M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
